/* global fetch, Response */
// proxy-provider.ts — AiClient implementation that calls the Supabase Edge
// Function proxy. The Gemini API key NEVER touches this file; the proxy holds it.
// Usage: createProxyClient(getAccessToken, getProxyUrl) → AiClient

import type {
  AiClient,
  AiConnectionResult,
  AiGenerateRequest,
  AiContent,
  AiContentPart,
  ToolTurnRequest,
  ToolTurn,
} from "./ai-client";
import { AiError } from "./ai-client";

// ── Internal helpers ──────────────────────────────────────────────────────────

/** Map HTTP status → AiErrorCode + Vietnamese user message. `detail` = proxy error text. */
function mapHttpError(status: number, detail?: string): AiError {
  if (status === 401) {
    return new AiError("unauthorized", "Phiên đăng nhập đã hết hạn — vui lòng đăng nhập lại.", status);
  }
  if (status === 403) {
    return new AiError("forbidden", "Tài khoản không có quyền dùng tính năng AI.", status);
  }
  if (status === 429) {
    return new AiError("rate_limited", "Quá nhiều yêu cầu — vui lòng thử lại sau 1 phút.", status);
  }
  // Surface the proxy/Gemini detail when present (diagnoses 400 on schema/tools).
  return new AiError("server_error", detail ? `Lỗi (${status}): ${detail}` : `Lỗi máy chủ (${status}) — vui lòng thử lại sau.`, status);
}

/** POST to the proxy; returns parsed JSON or throws AiError. */
async function proxyPost(
  url: string,
  token: string,
  body: unknown,
): Promise<unknown> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AiError(
      "network_error",
      "Không kết nối được — kiểm tra mạng và thử lại.",
      undefined,
    );
  }

  if (!res.ok) {
    let detail = "";
    try {
      const b = await res.json();
      if (b && typeof b.error === "string") detail = b.error;
    } catch {
      // non-JSON error body — ignore
    }
    throw mapHttpError(res.status, detail);
  }

  try {
    return await res.json();
  } catch {
    throw new AiError("parse_error", "Phản hồi không hợp lệ từ máy chủ AI.");
  }
}

/** Build the Gemini-style request body from AiGenerateRequest. */
function buildBody(req: AiGenerateRequest): Record<string, unknown> {
  const body: Record<string, unknown> = { contents: req.contents };
  const genConfig: Record<string, unknown> = { ...(req.generationConfig ?? {}) };

  if (req.responseSchema) {
    genConfig.responseMimeType = "application/json";
    genConfig.responseSchema = req.responseSchema;
  }

  if (Object.keys(genConfig).length > 0) body.generationConfig = genConfig;
  if (req.tools && req.tools.length > 0) body.tools = req.tools;

  return body;
}

/** Extract plain text from a Gemini generateContent response. */
function extractText(data: unknown): string {
  try {
    const d = data as {
      candidates?: Array<{
        content?: { parts?: Array<{ text?: string }> };
      }>;
    };
    return d.candidates?.[0]?.content?.parts?.[0]?.text ?? "";
  } catch {
    throw new AiError("parse_error", "Không đọc được nội dung phản hồi AI.");
  }
}

/**
 * Parse a Gemini generateContent response into a ToolTurn.
 * Each part is either { text } or { functionCall: { name, args } }.
 * Text parts are joined; functionCall parts become AiToolCall entries.
 */
function extractToolTurn(data: unknown): ToolTurn {
  type GeminiPart = {
    text?: string;
    functionCall?: { name: string; args: Record<string, unknown> };
    // Gemini 3: signature accompanying a functionCall (or text) part — must be echoed back.
    thoughtSignature?: string;
  };
  try {
    const d = data as {
      candidates?: Array<{
        content?: { parts?: GeminiPart[] };
      }>;
    };
    const parts: GeminiPart[] = d.candidates?.[0]?.content?.parts ?? [];
    const textParts: string[] = [];
    const toolCalls: ToolTurn["toolCalls"] = [];

    for (const part of parts) {
      if (part.functionCall) {
        toolCalls.push({
          name: part.functionCall.name,
          args: part.functionCall.args ?? {},
          ...(part.thoughtSignature ? { thoughtSignature: part.thoughtSignature } : {}),
        });
      } else if (typeof part.text === "string" && part.text) {
        textParts.push(part.text);
      }
    }

    return { text: textParts.join(""), toolCalls };
  } catch {
    throw new AiError("parse_error", "Không đọc được kết quả tool từ phản hồi AI.");
  }
}

/**
 * Build a Gemini tool-calling request body.
 * Sends: contents, tools (functionDeclarations), optional systemInstruction,
 * and generationConfig with temperature=0.4.
 * The proxy passes this body unchanged to Gemini generateContent.
 */
function buildToolBody(req: ToolTurnRequest): Record<string, unknown> {
  // Convert AiContent[] to Gemini-wire format (same shape — parts may have functionCall/functionResponse)
  const contents = req.contents.map((c: AiContent) => ({
    role: c.role,
    parts: c.parts.map((p: AiContentPart) => {
      if ("functionCall" in p && p.functionCall) {
        // Echo Gemini 3 thoughtSignature back on the SAME part, else Gemini 400s.
        return p.thoughtSignature
          ? { functionCall: p.functionCall, thoughtSignature: p.thoughtSignature }
          : { functionCall: p.functionCall };
      }
      if ("functionResponse" in p && p.functionResponse) {
        return { functionResponse: p.functionResponse };
      }
      return { text: (p as { text: string }).text };
    }),
  }));

  // Gemini rejects a function declaration whose `parameters` has empty/absent
  // `properties` — omit `parameters` entirely for no-arg tools (valid + safe).
  const functionDeclarations = req.tools.map((t) => {
    const params = t.parameters as { properties?: Record<string, unknown> } | undefined;
    const hasProps = params && params.properties && Object.keys(params.properties).length > 0;
    return hasProps ? t : { name: t.name, description: t.description };
  });

  const body: Record<string, unknown> = {
    contents,
    tools: [{ functionDeclarations }],
    generationConfig: { temperature: 0.4 },
  };

  if (req.systemInstruction) {
    body.systemInstruction = { parts: [{ text: req.systemInstruction }] };
  }

  return body;
}

// ── Factory ───────────────────────────────────────────────────────────────────

/**
 * Create an AiClient that routes all requests through the Supabase Edge Function
 * proxy. The proxy verifies the Supabase session and holds the Gemini API key.
 *
 * @param getAccessToken — async getter; returns the current Supabase JWT or null.
 * @param getProxyUrl   — returns the full proxy URL (e.g. from ai-config.ts).
 */
export function createProxyClient(
  getAccessToken: () => Promise<string | null>,
  getProxyUrl: () => string,
): AiClient {
  async function requireToken(): Promise<string> {
    const token = await getAccessToken();
    if (!token) {
      throw new AiError(
        "unauthorized",
        "Cần đăng nhập để dùng tính năng AI.",
      );
    }
    return token;
  }

  return {
    async generateJson<T>(req: AiGenerateRequest): Promise<T> {
      const token = await requireToken();
      const data = await proxyPost(getProxyUrl(), token, buildBody(req));
      try {
        const text = extractText(data);
        return JSON.parse(text) as T;
      } catch (err) {
        if (err instanceof AiError) throw err;
        throw new AiError("parse_error", "Phản hồi JSON từ AI không hợp lệ.");
      }
    },

    async generateText(req: AiGenerateRequest): Promise<string> {
      const token = await requireToken();
      const data = await proxyPost(getProxyUrl(), token, buildBody(req));
      return extractText(data);
    },

    async testConnection(): Promise<AiConnectionResult> {
      try {
        const token = await requireToken();
        const body = buildBody({
          contents: [{ role: "user", parts: [{ text: "ping" }] }],
          generationConfig: { maxOutputTokens: 8 },
        });
        const data = await proxyPost(getProxyUrl(), token, body) as {
          modelUsed?: string;
        };
        return { ok: true, modelUsed: data.modelUsed };
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Lỗi không xác định";
        return { ok: false, error: msg };
      }
    },

    async callWithTools(req: ToolTurnRequest): Promise<ToolTurn> {
      const token = await requireToken();
      const data = await proxyPost(getProxyUrl(), token, buildToolBody(req));
      return extractToolTurn(data);
    },
  };
}
